import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Close
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface TrendReportStatusDialogProps {
  open: boolean;
  onClose: () => void;
  trendReportId: number;
  currentStatus: string;
  marketName: string;
  currentNotes?: string | null;
  onStatusUpdated: () => void;
}

export const TrendReportStatusDialog: React.FC<TrendReportStatusDialogProps> = ({
  open,
  onClose,
  trendReportId,
  currentStatus,
  marketName,
  currentNotes,
  onStatusUpdated
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [selectedStatus, setSelectedStatus] = useState<'needs_adjustments' | 'completed' | 'cancelled'>('completed');
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | null>(null);

  const updateStatusMutation = useMutation({
    mutationFn: async () => {
      // Validate comments are provided for needs_adjustments
      if (selectedStatus === 'needs_adjustments' && !comments.trim()) {
        throw new Error('Comments are required when requesting adjustments');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/trend-report/${trendReportId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: selectedStatus,
            notes: comments.trim() || null
          })
        }
      );

      if (!response || !response.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to update status');
      }

      return response.json();
    },
    onSuccess: () => {
      onStatusUpdated();
      handleClose();
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to update status');
    }
  });

  const handleClose = () => {
    setSelectedStatus('completed');
    setComments('');
    setError(null);
    updateStatusMutation.reset();
    onClose();
  };

  const handleSubmit = () => {
    setError(null);
    updateStatusMutation.mutate();
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'purchased':
        return 'Awaiting Submission';
      case 'submitted':
        return 'Under Review';
      case 'needs_adjustments':
        return 'Needs Adjustments';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Update Report Status</Typography>
        <Typography variant="body2" color="text.secondary">
          Custom Comp Set Report #{trendReportId} - {marketName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {getStatusLabel(currentStatus)}
          </Typography>
        </Box>

        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel component="legend">New Status</FormLabel>
          <RadioGroup
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as 'needs_adjustments' | 'completed' | 'cancelled')}
          >
            <FormControlLabel
              value="needs_adjustments"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                  <Typography>Request Adjustments</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="completed"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography>Mark as Completed</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="cancelled"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Close sx={{ color: 'error.main', fontSize: 20 }} />
                  <Typography>Cancel Report</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Comments"
          placeholder={
            selectedStatus === 'needs_adjustments'
              ? 'Enter the adjustments needed (required)...'
              : selectedStatus === 'cancelled'
              ? 'Enter cancellation reason (optional)...'
              : 'Enter any comments for the customer (optional)...'
          }
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          error={selectedStatus === 'needs_adjustments' && !comments.trim()}
          helperText={
            selectedStatus === 'needs_adjustments' && !comments.trim()
              ? 'Comments are required when requesting adjustments'
              : 'These comments will be visible to the customer'
          }
          sx={{ mb: 2 }}
        />

        {currentNotes && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Previous Comments
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {currentNotes}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedStatus === 'needs_adjustments' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The customer will be notified that adjustments are needed and will see your comments.
          </Alert>
        )}

        {selectedStatus === 'completed' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The report will be marked as completed and the customer will be able to download any uploaded documents.
          </Alert>
        )}

        {selectedStatus === 'cancelled' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The report will be marked as cancelled. This action indicates the report has been cancelled and will not be completed.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={updateStatusMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            updateStatusMutation.isPending ||
            (selectedStatus === 'needs_adjustments' && !comments.trim())
          }
          startIcon={
            updateStatusMutation.isPending ? (
              <CircularProgress size={20} />
            ) : selectedStatus === 'completed' ? (
              <CheckCircle />
            ) : selectedStatus === 'cancelled' ? (
              <Close />
            ) : (
              <Warning />
            )
          }
          color={selectedStatus === 'completed' ? 'success' : selectedStatus === 'cancelled' ? 'error' : 'warning'}
        >
          {updateStatusMutation.isPending
            ? 'Updating...'
            : selectedStatus === 'completed'
            ? 'Mark as Completed'
            : selectedStatus === 'cancelled'
            ? 'Cancel Report'
            : 'Request Adjustments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};